import React, { useEffect, useState } from 'react';
import { useApiClient } from '../services/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import CustomLoader from './CustomLoader';

interface CurrentAttendeesProps {
  className?: string;
  compact?: boolean;
}

interface AttendeeData {
  id: string;
  name: string;
  email: string;
  linkedin: string;
  phone: string;
  telegram: string;
  roles: string[];
  skills: string[];
  hackdayCount: number;
  hackdayNames: string[];
  bountiesParticipated: number;
}

interface CurrentAttendeesData {
  attendees: AttendeeData[];
  totalAttendees: number;
  totalEvents: number;
}

export const CurrentAttendees: React.FC<CurrentAttendeesProps> = ({ className = '', compact = false }) => {
  const [stats, setStats] = useState<CurrentAttendeesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const apiClient = useApiClient();

  useEffect(() => {
    const fetchAttendees = async () => {
      try {
        setLoading(true);
        const response = await apiClient.getCurrentAttendees();

        if (response.success && response.data) {
          setStats(response.data);
          setError(null);
        } else {
          setError(response.error || 'Failed to fetch current attendees');
        }
      } catch (err) {
        setError('An error occurred while fetching attendees');
        console.error('Error fetching current attendees:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendees();
  }, [apiClient]);

  if (loading) {
    return <CustomLoader />;
  }

  if (error) {
    return (
      <div className={`w-full my-8 ${className}`}>
        <div className="text-center p-8 text-red-600">{error}</div>
      </div>
    );
  }

  if (!stats || !stats.attendees || stats.attendees.length === 0) {
    return (
      <div className={`w-full my-8 ${className}`}>
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100/50 border-2 border-dashed border-gray-300">
          <CardContent className="p-8 text-center">
            <div className="text-6xl opacity-50 mb-4">👥</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Participants Yet</h3>
            <p className="text-gray-600">You have no participants signed up to your events.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`w-full ${compact ? '' : 'my-8'} ${className}`}>
      <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-7xl mx-auto mb-8'}`}>
        {stats.attendees.map((attendee) => (
          <Card key={attendee.id} className={`bg-white hover:shadow-lg transition-shadow duration-300 ${compact ? 'border-l-4 border-l-blue-500' : ''}`}>
            <CardContent className={compact ? "p-3 flex items-center gap-3" : "p-4"}>
              {compact ? (
                // Compact View
                <>
                  <div className="text-2xl opacity-70 leading-none">👤</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-gray-900 truncate">{attendee.name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {attendee.hackdayCount} Event{attendee.hackdayCount !== 1 ? 's' : ''} • {attendee.bountiesParticipated} Bounties
                    </div>
                  </div>
                </>
              ) : (
                // Full View
                <>
                  {/* Attendee Header */}
                  <div className="flex items-start gap-3 mb-3 pb-3 border-b border-gray-100">
                    <div className="text-3xl opacity-70 leading-none mt-0.5">👤</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-base text-gray-900 truncate leading-tight mb-1">{attendee.name}</div>
                      <div className="text-xs text-gray-500">
                        {attendee.hackdayCount} Event{attendee.hackdayCount !== 1 ? 's' : ''} • {attendee.bountiesParticipated} Bounties
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="mb-3 space-y-1.5">
                    <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Contact</div>
                    {attendee.email && (
                      <div className="text-xs">
                        <a href={`mailto:${attendee.email}`} className="text-blue-600 hover:underline truncate block">
                          {attendee.email}
                        </a>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 text-xs">
                      {attendee.linkedin && (
                        <a href={attendee.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          LinkedIn
                        </a>
                      )}
                      {attendee.phone && (
                        <span className="text-gray-700">{attendee.phone}</span>
                      )}
                      {attendee.telegram && (
                        <span className="text-gray-700">{attendee.telegram}</span>
                      )}
                    </div>
                  </div>

                  {/* Skills */}
                  {attendee.skills && attendee.skills.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Skills</div>
                      <div className="flex flex-wrap gap-1">
                        {attendee.skills.slice(0, 4).map((skill, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs py-0 px-2 h-5">{skill}</Badge>
                        ))}
                        {attendee.skills.length > 4 && (
                          <Badge variant="outline" className="text-xs py-0 px-2 h-5">+{attendee.skills.length - 4}</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Events */}
                  {attendee.hackdayNames && attendee.hackdayNames.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">Events</div>
                      <div className="space-y-0.5">
                        {attendee.hackdayNames.slice(0, 2).map((name, idx) => (
                          <div key={idx} className="text-xs text-gray-700 truncate">{name}</div>
                        ))}
                        {attendee.hackdayNames.length > 2 && (
                          <div className="text-xs text-gray-500">+{attendee.hackdayNames.length - 2} more</div>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Summary Section - Hide in compact mode */}
      {!compact && (
        <div className="flex gap-6 justify-center flex-wrap p-5 bg-white border border-gray-200 rounded-lg shadow-sm max-w-lg mx-auto">
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Total Attendees</span>
            <span className="text-3xl font-bold text-gray-900">{stats.totalAttendees}</span>
          </div>
          <div className="flex flex-col items-center gap-1">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Active Events</span>
            <span className="text-3xl font-bold text-gray-900">{stats.totalEvents}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentAttendees;
